import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useAdminDisplays, DisplayWithOwner } from '@/hooks/useAdminDisplays';
import { toast } from 'sonner';
import {
  Plus, Save, RefreshCw, MapPin, Upload, AlertTriangle, CheckCircle,
  ExternalLink, Check, ChevronsUpDown, Calendar, Image as ImageIcon, Music, X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSpotifySearch } from '@/hooks/useSpotifySearch';
import { upsertDisplayYear } from '@/utils/displayYearUpsert';
import { SpotifyTrack } from '@/services/spotifyService';
import { supabase } from '@/integrations/supabase/client';
import ScheduleEditor from '@/components/displays/ScheduleEditor';
import { EnhancedSchedule } from '@/types/display';
import MediaUploadSection from '@/components/displays/edit/MediaUploadSection';
import HolidaySelector from '@/components/displays/HolidaySelector';
import { MapboxAddressInput, AddressData } from '@/components/MapboxAddressInput';
import SongsSection from '@/components/displays/edit/SongsSection';

interface DisplaySong {
  id?: string;
  title: string;
  artist: string;
  duration?: string;
  year_introduced: number;
  youtube_url?: string;
  display_year_id?: string;
  spotify_track_id?: string;
  spotify_preview_url?: string;
  album_cover_url?: string;
  genre?: string;
  spotify_popularity?: number;
}

interface DisplayEditorProps {
  preselectedDisplayId?: string | null;
}

export function DisplayEditor({ preselectedDisplayId }: DisplayEditorProps) {
  const {
    displays,
    loading,
    createDisplay,
    updateDisplay,
    uploadDisplayImage,
    addDisplayPhotos,
    removeDisplayPhoto,
    reorderDisplayPhotos,
    findDisplaysMissingInfo,
    findDuplicateDisplays
  } = useAdminDisplays();

  const [selectedDisplayId, setSelectedDisplayId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<DisplayWithOwner>>({});
  const [songs, setSongs] = useState<DisplaySong[]>([{
    title: '',
    artist: '',
    year_introduced: new Date().getFullYear(),
    youtube_url: ''
  }]);
  const [saving, setSaving] = useState(false);
  const [displayOpen, setDisplayOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const justSavedRef = useRef(false);
  const dragIndexRef = useRef<number | null>(null);

  // Track if form has been initialized for the current display
  const initializedDisplayRef = useRef<number | null>(null);

  // Update form data when displays list changes and we have a selected display
  useEffect(() => {
    if (selectedDisplayId && !isCreating && !saving && !justSavedRef.current && !isEditing) {
      const display = displays.find(d => d.id === selectedDisplayId);
      if (display) {
        const shouldSync =
          initializedDisplayRef.current !== selectedDisplayId ||
          (justSavedRef.current === false && initializedDisplayRef.current === selectedDisplayId);

        if (shouldSync) {
          console.log('Syncing form with DB values for display:', selectedDisplayId);
          console.log('Display data being synced:', {
            id: display.id,
            name: display.name,
            location: display.location,
            hasName: !!display.name,
            hasLocation: !!display.location,
            displayKeys: Object.keys(display)
          });
          setFormData(display);
          initializedDisplayRef.current = selectedDisplayId;
        }
      }
    }

    // Reset the justSaved flag after a delay to allow for future syncing
    if (justSavedRef.current) {
      const timer = setTimeout(() => {
        justSavedRef.current = false;
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [displays, selectedDisplayId, isCreating, saving, isEditing]);

  // Real-time debug logging for button disabled state
  useEffect(() => {
    console.log('=== BUTTON STATE CHECK (Real-time) ===');
    console.log('Button disabled state:', {
      saving,
      hasName: !!formData.name,
      name: formData.name,
      isDisabled: saving || !formData.name,
      isEditing,
      selectedDisplayId
    });
  }, [formData, saving, isEditing, selectedDisplayId]);

  // Reset form when switching displays
  const handleDisplaySelect = useCallback((displayId: string) => {
    if (displayId === 'new') {
      setIsCreating(true);
      setSelectedDisplayId(null);
      setFormData({
        name: '',
        description: '',
        location: '',
        status: 'active',
        featured: false
      });
      setSongs([{
        title: '',
        artist: '',
        year_introduced: new Date().getFullYear(),
        youtube_url: ''
      }]);
      initializedDisplayRef.current = null;
    } else {
      const id = parseInt(displayId);
      setIsCreating(false);
      setSelectedDisplayId(id);
      const display = displays.find(d => d.id === id);
      if (display) {
        setFormData(display);
        initializedDisplayRef.current = id;
        // Load songs for this display
        loadDisplaySongs(id);
      }
    }
  }, [displays]);

  // Load songs for a display
  const loadDisplaySongs = async (displayId: number) => {
    try {
      // First try current year; use maybeSingle to avoid 406 when no row
      const currentYear = new Date().getFullYear();
      let { data, error } = await supabase
        .from('display_years')
        .select(`
          id,
          year,
          display_songs (
            id,
            title,
            artist,
            duration,
            year_introduced,
            video_url
          )
        `)
        .eq('display_id', displayId)
        .eq('year', currentYear)
        .maybeSingle();

      // Fallback: fetch the most recent year if current year not found
      if (!data && !error) {
        const fallback = await supabase
          .from('display_years')
          .select(`
            id,
            year,
            display_songs (
              id,
              title,
              artist,
              duration,
              year_introduced,
              video_url
            )
          `)
          .eq('display_id', displayId)
          .order('year', { ascending: false })
          .limit(1)
          .maybeSingle();
        data = fallback.data as any;
        error = fallback.error as any;
      }

      if (!error && data?.display_songs) {
        const loadedSongs = data.display_songs.map((song: any) => ({
          id: song.id,
          title: song.title,
          artist: song.artist,
          duration: song.duration,
          year_introduced: song.year_introduced,
          youtube_url: song.video_url,
          display_year_id: data.id
        }));
        setSongs(loadedSongs.length > 0 ? loadedSongs : [{
          title: '',
          artist: '',
          year_introduced: new Date().getFullYear(),
          youtube_url: ''
        }]);
      } else {
        setSongs([{
          title: '',
          artist: '',
          year_introduced: new Date().getFullYear(),
          youtube_url: ''
        }]);
      }
    } catch (error) {
      console.error('Error loading songs:', error);
      setSongs([{
        title: '',
        artist: '',
        year_introduced: new Date().getFullYear(),
        youtube_url: ''
      }]);
    }
  };

  // Handle preselected display from URL parameter
  useEffect(() => {
    if (preselectedDisplayId && displays.length > 0 && !selectedDisplayId) {
      const id = parseInt(preselectedDisplayId);
      const display = displays.find(d => d.id === id);
      if (display) {
        console.log('Auto-selecting display from URL parameter:', preselectedDisplayId);
        handleDisplaySelect(preselectedDisplayId);
      } else {
        console.warn('Preselected display not found:', preselectedDisplayId);
      }
    }
  }, [preselectedDisplayId, displays, selectedDisplayId, handleDisplaySelect]);

  // Handle form field changes
  const handleFieldChange = (field: string, value: unknown) => {
    // Mark form as being actively edited
    setIsEditing(true);

    // Convert empty strings to null for optional fields
    const processedValue = (typeof value === 'string' && value.trim() === '') ? null : value;

    // Better logging for objects/arrays
    const valueStr = typeof value === 'object' && value !== null
      ? JSON.stringify(value).substring(0, 100) + (JSON.stringify(value).length > 100 ? '...' : '')
      : value;
    const processedStr = typeof processedValue === 'object' && processedValue !== null
      ? JSON.stringify(processedValue).substring(0, 100) + (JSON.stringify(processedValue).length > 100 ? '...' : '')
      : processedValue;
    console.log(`Field changed: ${field} = ${valueStr} -> processed: ${processedStr}`);

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  // Handle file upload for display image
  const handleImageUpload = async (file: File) => {
    if (!selectedDisplayId && !isCreating) {
      toast.error('Please select or create a display first');
      return;
    }

    try {
      const displayId = selectedDisplayId || 'temp-' + Date.now();
      const result = await uploadDisplayImage(file, displayId);
      
      setFormData(prev => ({
        ...prev,
        image_url: result.url
      }));
      
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
      console.error('Error uploading image:', error);
    }
  };

  // Handle schedule as JSON object
  const handleScheduleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...(prev.schedule as any || {}),
        [field]: value
      }
    }));
  };

  // Stable callback for ScheduleEditor to prevent effect loops
  const handleScheduleUpdate = useCallback((schedule: EnhancedSchedule) => {
    setFormData(prev => ({
      ...prev,
      schedule
    }));
  }, []);


  // Save display
  const handleSave = async () => {
    console.log('=== SAVE BUTTON CLICKED ===');
    console.log('Form data being saved:', formData);
    console.log('Selected display ID:', selectedDisplayId);
    console.log('Is creating:', isCreating);

    if (!formData.name) {
      toast.error('Display name is required');
      return;
    }

    setSaving(true);
    try {
      if (isCreating) {
        console.log('Creating new display...');
        const newDisplay = await createDisplay(formData);
        console.log('New display created:', newDisplay);
        toast.success('Display created successfully');
        
        // Switch to editing the newly created display
        setIsCreating(false);
        setSelectedDisplayId(newDisplay.id);
        justSavedRef.current = true;
        setIsEditing(false);

        // Save songs for the newly created display
        if (newDisplay.id && songs.some(s => s.title?.trim() && s.artist?.trim())) {
          await saveDisplaySongs(newDisplay.id);
        }
      } else if (selectedDisplayId) {
        console.log('Updating existing display...');
        const result = await updateDisplay(selectedDisplayId, formData);
        console.log('Display update result:', result);
        toast.success('Display updated successfully');

        justSavedRef.current = true;
        setIsEditing(false);
        
        // Save songs for the existing display
        if (songs.some(s => s.title?.trim() && s.artist?.trim())) {
          await saveDisplaySongs(selectedDisplayId);
        }
      }
    } catch (error) {
      toast.error('Failed to save display');
      console.error('Error saving display:', error);
    } finally {
      setSaving(false);
      console.log('=== SAVE COMPLETED ===');
    }
  };

  // Save songs for a display
  const saveDisplaySongs = async (displayId: number) => {
    try {
      console.log('=== SAVING SONGS ===');
      console.log('Display ID:', displayId);
      console.log('Songs to save:', songs);
      
      // Get or create display year using the utility function
      const displayYear = await upsertDisplayYear(displayId);
      
      console.log('Display year result:', displayYear);

      if (!displayYear) {
        console.error('Failed to get or create display year');
        return;
      }

      // Delete existing songs for this display year
      const { error: deleteError } = await supabase
        .from('display_songs')
        .delete()
        .eq('display_year_id', displayYear.id);

      if (deleteError) {
        console.error('Error deleting existing songs:', deleteError);
        return;
      }

      // Insert new songs
      const validSongs = songs.filter(song =>
        song.title?.trim() &&
        song.artist?.trim() &&
        song.year_introduced
      ).map(song => ({
        title: song.title.trim(),
        artist: song.artist.trim(),
        duration: song.duration?.trim() || null,
        year_introduced: song.year_introduced,
        video_url: song.youtube_url?.trim() || null,
        display_year_id: displayYear.id,
        spotify_track_id: song.spotify_track_id || null,
        spotify_preview_url: song.spotify_preview_url || null,
        album_cover_url: song.album_cover_url || null,
        genre: song.genre || null,
        spotify_popularity: song.spotify_popularity || null
      }));

      console.log('Valid songs to insert:', validSongs);

      if (validSongs.length > 0) {
        const { data: insertedSongs, error: songsError } = await supabase
          .from('display_songs')
          .insert(validSongs)
          .select();

        if (songsError) {
          console.error('Error saving songs:', songsError);
          throw songsError;
        } else {
          console.log('Successfully saved songs:', insertedSongs);
        }
      } else {
        console.log('No valid songs to save');
      }
      
      console.log('=== SONGS SAVE COMPLETED ===');
    } catch (error) {
      console.error('Error in saveDisplaySongs:', error);
      throw error;
    }
  };

  const selectedDisplay = displays.find(d => d.id === selectedDisplayId);

  return (
    <div className="space-y-6">
      {/* Display Selector */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="display-select" className="text-base font-medium">
            Select Display to Edit
          </Label>
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              {displays.length} displays available
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const missing = await findDisplaysMissingInfo();
                if (missing.length > 0) {
                  const message = missing.map(d => `"${d.name}" (missing: ${
                    [
                      !d.description && 'description',
                      !d.image_url && 'image',
                      !d.holiday_type && 'holiday type',
                      !d.city && 'city'
                    ].filter(Boolean).join(', ')
                  })`).join('\n');
                  alert(`Found ${missing.length} displays missing info:\n\n${message}`);
                } else {
                  alert('All displays have complete information!');
                }
              }}
              className="text-xs"
            >
              Check Missing Info
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const duplicates = await findDuplicateDisplays();
                if (duplicates.length > 0) {
                  const message = duplicates.map(d => `"${d.displays[0].name}" (${d.displays.length} copies)`).join('\n');
                  alert(`Found ${duplicates.length} sets of potential duplicate displays:\n\n${message}`);
                } else {
                  alert('No duplicate displays found!');
                }
              }}
              className="text-xs"
            >
              Check Duplicates
            </Button>
          </div>
        </div>
        
        <Popover open={displayOpen} onOpenChange={setDisplayOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={displayOpen}
              className="w-full justify-between"
            >
              {selectedDisplayId 
                ? displays.find(d => d.id === selectedDisplayId)?.name
                : isCreating
                  ? "Create New Display"
                  : "Select a display or create new..."
              }
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0">
            <Command>
              <CommandInput placeholder="Search displays..." />
              <CommandEmpty>No display found.</CommandEmpty>
              <CommandList>
                <CommandGroup>
                  <CommandItem
                    value="new"
                    onSelect={() => {
                      handleDisplaySelect('new');
                      setDisplayOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isCreating ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <Plus className="mr-2 h-4 w-4" />
                    Create New Display
                  </CommandItem>
                  {displays
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((display) => (
                    <CommandItem
                      key={display.id}
                      value={`${display.name} ${display.location} ${display.id}`}
                      onSelect={() => {
                        handleDisplaySelect(display.id.toString());
                        setDisplayOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedDisplayId === display.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex items-center justify-between w-full">
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="truncate">{display.name}</span>
                          <span className="text-xs text-muted-foreground truncate">
                            {display.location} • ID: {display.id}
                          </span>
                        </div>
                        <div className="flex gap-1 ml-2 shrink-0">
                          {display.featured && (
                            <Badge variant="secondary" className="text-xs">Featured</Badge>
                          )}
                          {display.image_url && (
                            <Badge variant="secondary" className="text-xs">📷</Badge>
                          )}
                          {display.holiday_type && (
                            <Badge variant="secondary" className="text-xs">
                              {display.holiday_type.charAt(0).toUpperCase()}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Preview Button - only show when editing existing display */}
      {selectedDisplayId && !isCreating && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(`/display/${selectedDisplayId}`, '_blank')}
            className="flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Preview on Site
          </Button>
        </div>
      )}

      {(selectedDisplayId || isCreating) && (
        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Display Name *</Label>
                  <Input
                    id="name"
                    value={formData.name || ''}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    placeholder="Enter display name"
                  />
                </div>
                <div>
                  <Label htmlFor="display_type">Display Type</Label>
                  <Input
                    id="display_type"
                    value={formData.display_type || ''}
                    onChange={(e) => handleFieldChange('display_type', e.target.value)}
                    placeholder="e.g., Musical Light Show, Projection Mapping"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Describe this holiday display..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="year_started">Year Started</Label>
                  <Input
                    id="year_started"
                    type="number"
                    min="1950"
                    max={new Date().getFullYear()}
                    value={formData.year_started || ''}
                    onChange={(e) => handleFieldChange('year_started', parseInt(e.target.value) || null)}
                    placeholder="2020"
                  />
                </div>
                <div>
                  <Label htmlFor="light_count">Number of Lights</Label>
                  <Input
                    id="light_count"
                    type="number"
                    min="1"
                    value={formData.light_count || ''}
                    onChange={(e) => handleFieldChange('light_count', parseInt(e.target.value) || null)}
                    placeholder="e.g. 20000"
                  />
                </div>
              </div>

              {/* Holidays Celebrated - Multiple Selection */}
              <div>
                <Label className="text-base font-medium">Holidays Celebrated</Label>
                <p className="text-sm text-muted-foreground mb-3">Select all holidays this display celebrates</p>
                <HolidaySelector
                  value={formData.holidays || []}
                  onChange={(holidays) => handleFieldChange('holidays', holidays)}
                  mode="checkbox"
                  layout="grid"
                  showIcons={true}
                  showSections={true}
                />
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={formData.status || 'active'} 
                  onValueChange={(value) => handleFieldChange('status', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Location Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="location">Full Address *</Label>
                <MapboxAddressInput
                  value={formData.location || ''}
                  onChange={(value) => handleFieldChange('location', value)}
                  onAddressSelect={(addressData: AddressData) => {
                    // Auto-fill all fields from Mapbox data
                    handleFieldChange('location', addressData.fullAddress);
                    if (addressData.city) handleFieldChange('city', addressData.city);
                    if (addressData.state) handleFieldChange('state', addressData.state);
                    if (addressData.zipCode) handleFieldChange('zip_code', addressData.zipCode);
                    if (addressData.latitude) handleFieldChange('latitude', addressData.latitude);
                    if (addressData.longitude) handleFieldChange('longitude', addressData.longitude);
                  }}
                  placeholder="Start typing address..."
                  required
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Start typing to see address suggestions. Selecting an address will auto-fill all location fields.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city || ''}
                    onChange={(e) => handleFieldChange('city', e.target.value)}
                    placeholder="City name"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.state || ''}
                    onChange={(e) => handleFieldChange('state', e.target.value)}
                    placeholder="State or Province"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="latitude">Latitude</Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="0.000001"
                    value={formData.latitude || ''}
                    onChange={(e) => handleFieldChange('latitude', parseFloat(e.target.value) || null)}
                    placeholder="40.7128"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude">Longitude</Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="0.000001"
                    value={formData.longitude || ''}
                    onChange={(e) => handleFieldChange('longitude', parseFloat(e.target.value) || null)}
                    placeholder="-74.0060"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="parking_other">Parking Information</Label>
                <Textarea
                  id="parking_other"
                  value={formData.parking_other || ''}
                  onChange={(e) => handleFieldChange('parking_other', e.target.value)}
                  placeholder="Parking instructions and availability..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Media & Schedule */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Media & Basic Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="image_url">Image URL</Label>
                  <Input
                    id="image_url"
                    value={formData.image_url || ''}
                    onChange={(e) => handleFieldChange('image_url', e.target.value)}
                    placeholder="https://example.com/display.jpg"
                  />
                </div>
                <div>
                  <Label htmlFor="video">Video URL</Label>
                  <Input
                    id="video"
                    value={formData.video || ''}
                    onChange={(e) => handleFieldChange('video', e.target.value)}
                    placeholder="https://example.com/video.mp4"
                  />
                </div>
              </div>

              <div className="mt-3">
                <MediaUploadSection
                  images={(Array.isArray((formData as any).photos) ? (formData as any).photos : []) as string[]}
                  uploadingImages={false}
                  maxImages={10}
                  onImagesChange={async (imgs) => {
                    handleFieldChange('photos' as any, imgs as any);
                    if (selectedDisplayId && imgs) {
                      await reorderDisplayPhotos(selectedDisplayId, imgs);
                    }
                    if (!formData.image_url && imgs.length > 0) {
                      handleFieldChange('image_url', imgs[0]);
                    }
                  }}
                  onUploadImages={async (files: FileList) => {
                    const current = Array.isArray((formData as any).photos) ? (formData as any).photos as string[] : [];
                    const uploadedUrls: string[] = [];
                    for (const f of Array.from(files)) {
                      const result = await uploadDisplayImage(f, formData.id || 'new');
                      uploadedUrls.push(result.url);
                    }
                    const updated = [...current, ...uploadedUrls].slice(0, 10);
                    handleFieldChange('photos' as any, updated as any);
                    if (selectedDisplayId) {
                      await addDisplayPhotos(selectedDisplayId, uploadedUrls);
                    }
                    if (!formData.image_url && updated.length > 0) {
                      handleFieldChange('image_url', updated[0]);
                    }
                  }}
                />

                {/* Thumbnail grid is now provided by the unified MediaUploadSection above */}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="display_hours">Display Hours (Legacy)</Label>
                  <Input
                    id="display_hours"
                    value={formData.display_hours || ''}
                    onChange={(e) => handleFieldChange('display_hours', e.target.value)}
                    placeholder="5:00 PM - 10:00 PM daily"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Use detailed schedule editor below for better control
                  </p>
                </div>
                <div>
                  <Label htmlFor="opens_closes">Season Dates (Legacy)</Label>
                  <Input
                    id="opens_closes"
                    value={formData.opens_closes || ''}
                    onChange={(e) => handleFieldChange('opens_closes', e.target.value)}
                    placeholder="November 25 - January 5"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fm_station">FM Station</Label>
                  <Input
                    id="fm_station"
                    value={formData.fm_station || ''}
                    onChange={(e) => handleFieldChange('fm_station', e.target.value)}
                    placeholder="FM 88.1"
                  />
                </div>
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={formData.featured || false}
                    onChange={(e) => handleFieldChange('featured', e.target.checked)}
                  />
                  <Label htmlFor="featured">Featured Display</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={formData.website || ''}
                    onChange={(e) => handleFieldChange('website', e.target.value)}
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="facebook">Facebook Page</Label>
                  <Input
                    id="facebook"
                    value={formData.facebook || ''}
                    onChange={(e) => handleFieldChange('facebook', e.target.value)}
                    placeholder="https://facebook.com/displayname"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Advanced Schedule Editor */}
          <ScheduleEditor
            schedule={formData.schedule || null}
            city={formData.city}
            state={formData.state}
            onChange={handleScheduleUpdate}
          />

          {/* Songs Management */}
          <SongsSection
            songs={songs}
            maxSongs={100}
            onSongsChange={setSongs}
          />

          {/* Actions */}
          <div className="flex gap-4">
            <Button
              onClick={() => {
                console.log('=== BUTTON CLICK DEBUG ===');
                console.log('Button disabled state:', {
                  saving,
                  hasName: !!formData.name,
                  name: formData.name,
                  isDisabled: saving || !formData.name
                });
                handleSave();
              }}
              disabled={saving || !formData.name}
              className="flex-1"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isCreating ? 'Create Display' : 'Update Display'}
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

